/* eslint-disable camelcase */
// Allowlist mapping each supported URL query param to its form-data key.
const UTM_PARAM_MAP = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
};
/* eslint-enable camelcase */

export const STORAGE_KEY = 'askVaUtmParams';

// Accessing window.sessionStorage can throw (e.g. SecurityError in sandboxed
// iframes or privacy modes), so resolve it defensively.
const getDefaultStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Parse allowlisted UTM params from a URL query string.
 *
 * @param {string | null | undefined} search - A `location.search` string (e.g. `?utm_source=x`).
 * @returns {Object} A flat object of present, non-empty UTM params keyed by their
 *   form-data names (e.g. `{ utmSource: 'x' }`). Absent or blank params are omitted.
 */
export const parseUtmParams = search => {
  const params = new URLSearchParams(search);

  return Object.entries(UTM_PARAM_MAP).reduce((acc, [urlKey, formKey]) => {
    const value = params.get(urlKey);
    if (value) {
      acc[formKey] = value;
    }

    return acc;
  }, {});
};

/**
 * Read the persisted UTM params from storage.
 *
 * @param {Storage} [storage] - Web Storage instance to read from. Defaults to
 *   `sessionStorage` when available.
 * @returns {Object} The stored UTM params, or an empty object if none are set,
 *   storage is unavailable, or the stored value is missing/corrupt.
 */
export const readStoredUtmParams = (storage = getDefaultStorage()) => {
  try {
    return JSON.parse(storage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

/**
 * Persist UTM params, merging with any already stored so previously captured
 * values survive a later param-less navigation. No-ops when `params` is empty
 * or storage is unavailable.
 *
 * @param {Object} params - UTM params keyed by their form-data names.
 * @param {Storage} [storage] - Web Storage instance to write to. Defaults to
 *   `sessionStorage` when available.
 * @returns {void}
 */
export const storeUtmParams = (params, storage = getDefaultStorage()) => {
  if (!storage || !params || Object.keys(params).length === 0) return;

  try {
    const merged = { ...readStoredUtmParams(storage), ...params };
    storage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // storage unavailable or quota exceeded; nothing to persist
  }
};

/**
 * Remove the persisted UTM params from storage. No-ops when storage is
 * unavailable.
 *
 * @param {Storage} [storage] - Web Storage instance to clear. Defaults to
 *   `sessionStorage` when available.
 * @returns {void}
 */
export const clearStoredUtmParams = (storage = getDefaultStorage()) => {
  if (!storage) return;

  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable; nothing to clear
  }
};
